import React, { useState } from 'react';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { UserPlus, Save, Loader2, Building2, Phone, Mail, Globe, MapPin, Briefcase } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
 
const CONTACT_ROLES = [
  { value: 'broker', label: 'Broker' }, { value: 'buyer', label: 'Acheteur' },
  { value: 'seller', label: 'Vendeur' }, { value: 'business_lawyer', label: 'Business Lawyer' },
  { value: 'immigration_attorney', label: 'Immigration Attorney' }, { value: 'lender', label: 'Lender' },
  { value: 'accountant', label: 'Comptable' }, { value: 'consultant', label: 'Consultant' },
  { value: 'investor', label: 'Investisseur' }, { value: 'other', label: 'Autre' },
];
 
const CONVERSION_STATUSES = [
  { value: 'contacted', label: 'Contacté' }, { value: 'interested', label: 'Intéressé' },
  { value: 'meeting_scheduled', label: 'RDV planifié' }, { value: 'converted', label: 'Converti' },
  { value: 'not_interested', label: 'Non intéressé' }, { value: 'follow_up', label: 'À recontacter' },
];
 
interface MarketingContactFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved?: () => void;
  prefillPhone?: string;
}
 
export function MarketingContactForm({ isOpen, onClose, onSaved, prefillPhone }: MarketingContactFormProps) {
  const { user } = useAuth();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    first_name: '', last_name: '', role: 'broker', company_name: '',
    phone: prefillPhone || '', email: '', address: '', website: '',
    specialty: '', notes: '', conversion_status: 'contacted', source: 'outbound_call',
  });
 
  const update = (field: string, value: string) => setForm(prev => ({ ...prev, [field]: value }));
 
  const handleSave = async () => {
    if (!form.first_name.trim() || !form.last_name.trim()) { toast.error('Prénom et nom requis'); return; }
    setSaving(true);
    try {
      await addDoc(collection(db, 'marketingContacts'), {
        firstName: form.first_name.trim(),
        lastName: form.last_name.trim(),
        role: form.role,
        companyName: form.company_name || null,
        phone: form.phone || null,
        email: form.email || null,
        address: form.address || null,
        website: form.website || null,
        specialty: form.specialty || null,
        notes: form.notes || null,
        conversionStatus: form.conversion_status,
        source: form.source,
        contactedBy: user?.id || '',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      toast.success('Contact marketing enregistré');
      setForm({ first_name: '', last_name: '', role: 'broker', company_name: '', phone: '', email: '', address: '', website: '', specialty: '', notes: '', conversion_status: 'contacted', source: 'outbound_call' });
      onSaved?.();
      onClose();
    } catch (err) {
      console.error(err);
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };
 
  return (
    <Dialog open={isOpen} onOpenChange={open => !open && onClose()}>
      <DialogContent className="bg-[hsl(220,20%,12%)] border-white/10 text-white max-w-lg max-h-[85vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-sm"><UserPlus className="w-4 h-4 text-emerald-400" />Nouveau contact marketing</DialogTitle>
        </DialogHeader>
        <ScrollArea className="max-h-[60vh] pr-2">
          <div className="space-y-4 py-1">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-[11px] text-white/50">Prénom *</Label><Input value={form.first_name} onChange={e => update('first_name', e.target.value)} placeholder="Jean" className="h-9 text-sm bg-white/5 border-white/10 text-white placeholder:text-white/30 mt-1" /></div>
              <div><Label className="text-[11px] text-white/50">Nom *</Label><Input value={form.last_name} onChange={e => update('last_name', e.target.value)} placeholder="Dupont" className="h-9 text-sm bg-white/5 border-white/10 text-white placeholder:text-white/30 mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-[11px] text-white/50">Rôle *</Label>
                <Select value={form.role} onValueChange={v => update('role', v)}>
                  <SelectTrigger className="h-9 text-sm bg-white/5 border-white/10 text-white mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[hsl(220,20%,15%)] border-white/10 text-white">{CONTACT_ROLES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-[11px] text-white/50 flex items-center gap-1"><Building2 className="w-3 h-3" /> Compagnie</Label><Input value={form.company_name} onChange={e => update('company_name', e.target.value)} placeholder="Acme Inc." className="h-9 text-sm bg-white/5 border-white/10 text-white placeholder:text-white/30 mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-[11px] text-white/50 flex items-center gap-1"><Phone className="w-3 h-3" /> Téléphone</Label><Input value={form.phone} onChange={e => update('phone', e.target.value)} placeholder="+1 514 555-1234" className="h-9 text-sm bg-white/5 border-white/10 text-white placeholder:text-white/30 mt-1" /></div>
              <div><Label className="text-[11px] text-white/50 flex items-center gap-1"><Mail className="w-3 h-3" /> Courriel</Label><Input value={form.email} onChange={e => update('email', e.target.value)} placeholder="jean@acme.com" className="h-9 text-sm bg-white/5 border-white/10 text-white placeholder:text-white/30 mt-1" /></div>
            </div>
            <div><Label className="text-[11px] text-white/50 flex items-center gap-1"><Briefcase className="w-3 h-3" /> Spécialité</Label><Input value={form.specialty} onChange={e => update('specialty', e.target.value)} placeholder="Immigration, M&A..." className="h-9 text-sm bg-white/5 border-white/10 text-white placeholder:text-white/30 mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-[11px] text-white/50">Statut de conversion</Label>
                <Select value={form.conversion_status} onValueChange={v => update('conversion_status', v)}>
                  <SelectTrigger className="h-9 text-sm bg-white/5 border-white/10 text-white mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[hsl(220,20%,15%)] border-white/10 text-white">{CONVERSION_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-[11px] text-white/50">Source</Label>
                <Select value={form.source} onValueChange={v => update('source', v)}>
                  <SelectTrigger className="h-9 text-sm bg-white/5 border-white/10 text-white mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[hsl(220,20%,15%)] border-white/10 text-white">
                    {[['outbound_call','Appel sortant'],['inbound_call','Appel entrant'],['sms','SMS'],['email','Email'],['referral','Référence'],['website','Site web'],['other','Autre']].map(([v,l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label className="text-[11px] text-white/50">Notes</Label><Textarea value={form.notes} onChange={e => update('notes', e.target.value)} placeholder="Informations collectées lors du contact..." className="min-h-[80px] text-sm bg-white/5 border-white/10 text-white placeholder:text-white/30 mt-1" /></div>
          </div>
        </ScrollArea>
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={onClose} className="text-white/50 hover:text-white">Annuler</Button>
          <Button size="sm" onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-500 text-white">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Save className="w-3.5 h-3.5 mr-1" />}
            Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
 